<script lang="js">
import Swal from "sweetalert2";
import frameImage from "@/assets/img/matric-frame.png";

export default {
  name: "MatricFrame",
  data() {
    return {
      logger: null,
      frameSrc: frameImage,
      uploadedFile: null,
      outputUrl: null,
      isProcessing: false,
      canvasWidth: 1080,
      canvasHeight: 1350,
      photoBox: {
        xRatio: 0.06,
        yRatio: 0.06,
        sizeRatio: 0.88,
      },
    };
  },
  created() {
    this.logger = this.$createLogger("MatricFrame");
  },
  mounted() {
    this.renderFrameOnly();
  },
  methods: {
    triggerFileInput() {
      if (this.$refs.fileInput) {
        this.$refs.fileInput.click();
      } else {
        this.logger.warn("File input not ready yet");
      }
    },

    async onFileChange(event) {
      const file = event.target.files?.[0];
      if (!file) return;

      if (!file.type.startsWith("image/")) {
        Swal.fire("Invalid file", "Please upload an image file.", "error");
        return;
      }

      this.uploadedFile = file;
      await this.renderFrame();
    },

    async renderFrame() {
      if (!this.uploadedFile) return;

      this.isProcessing = true;
      this.outputUrl = null;

      try {
        const userImage = await this.loadImage(URL.createObjectURL(this.uploadedFile));
        const frame = await this.loadImage(this.frameSrc);

        const canvas = this.$refs.frameCanvas;
        const ctx = canvas.getContext("2d");

        const width = this.canvasWidth;
        const height = this.canvasHeight;

        canvas.width = width;
        canvas.height = height;

        // Clear canvas
        ctx.clearRect(0, 0, width, height);

        // Compute photo placeholder (square) from ratios
        const boxSize = width * this.photoBox.sizeRatio;
        const boxX = width * this.photoBox.xRatio;
        const boxY = height * this.photoBox.yRatio;

        // Clip to placeholder area and draw user image on top
        ctx.save();
        ctx.beginPath();
        ctx.rect(boxX, boxY, boxSize, boxSize);
        ctx.clip();

        // Draw user image to cover the square box (center-crop)
        const scale = Math.max(boxSize / userImage.width, boxSize / userImage.height);
        const drawWidth = userImage.width * scale;
        const drawHeight = userImage.height * scale;
        const dx = boxX + (boxSize - drawWidth) / 2;
        const dy = boxY + (boxSize - drawHeight) / 2;
        ctx.drawImage(userImage, dx, dy, drawWidth, drawHeight);
        ctx.restore();

        // Draw frame overlay on top
        ctx.drawImage(frame, 0, 0, width, height);

        // Export final image
        this.outputUrl = canvas.toDataURL("image/png");
      } catch (error) {
        this.logger.error("Failed to render frame:", error);
        Swal.fire("Error", "Failed to process the image. Please try again.", "error");
      } finally {
        this.isProcessing = false;
      }
    },

    async renderFrameOnly() {
      try {
        const frame = await this.loadImage(this.frameSrc);
        const canvas = this.$refs.frameCanvas;
        const ctx = canvas.getContext("2d");

        const width = this.canvasWidth;
        const height = this.canvasHeight;

        canvas.width = width;
        canvas.height = height;

        ctx.clearRect(0, 0, width, height);
        ctx.drawImage(frame, 0, 0, width, height);
      } catch (error) {
        this.logger.error("Failed to render frame preview:", error);
      }
    },

    loadImage(src) {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = src;
      });
    },

    downloadImage() {
      if (!this.outputUrl) {
        Swal.fire("No image", "Please upload a photo first.", "info");
        return;
      }

      const link = document.createElement("a");
      link.href = this.outputUrl;
      link.download = "matric-frame.png";
      link.click();
    },
  },
};
</script>

<template>
  <div class="p-4 text-start">
    <h4 class="fw-bold">Matric Frame</h4>
    <p class="text-muted">
      Click the preview to upload your photo, then download and share!
    </p>
    <hr />

    <input
      ref="fileInput"
      type="file"
      class="d-none"
      accept="image/*"
      @change="onFileChange"
    />

    <div class="row g-4">
      <div class="col-lg-5">
        <div class="card shadow-sm">
          <div class="card-body">
            <h6 class="fw-bold mb-3">Instructions</h6>
            <ol class="instruction-list">
              <li>Click on the preview area to upload your photo</li>
              <li>Download your framed image using the button below</li>
              <li>Post on social media and tag us:</li>
            </ol>

            <div class="social-tags mt-3">
              <div class="mb-2">
                <i class="bi bi-instagram text-danger me-2"></i>
                <strong>Instagram:</strong>
                <a
                  href="https://www.instagram.com/firstcitypoly/"
                  class="text-primary text-decoration-none"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  @firstcitypoly
                </a>
                <span class=""></span>
              </div>
              <div class="mb-2">
                <i class="bi bi-twitter text-info me-2"></i>
                <strong>Twitter:</strong>
                <a
                  href="https://x.com/firstcitypoly/"
                  target="_blank"
                  class="text-primary text-decoration-none"
                  rel="noopener noreferrer"
                >
                  @firstcitypoly
                </a>
              </div>
              <div class="mb-2">
                <i class="bi bi-facebook text-primary me-2"></i>
                <strong>Facebook:</strong>
                <a
                  href="https://www.facebook.com/firstcitypoly"
                  target="_blank"
                  class="text-primary text-decoration-none"
                >
                  facebook.com/firstcitypoly
                </a>
              </div>
            </div>

            <button
              class="btn btn-primary w-100 mt-4"
              :disabled="!uploadedFile || isProcessing"
              @click="downloadImage"
            >
              <i class="bi bi-download me-1"></i>
              Download Image
            </button>
          </div>
        </div>
      </div>

      <div class="col-lg-7">
        <div class="card shadow-sm">
          <div class="card-body">
            <div class="d-flex align-items-center justify-content-between">
              <h6 class="fw-bold mb-3">Preview</h6>
              <span v-if="isProcessing" class="text-muted small">
                Processing...
              </span>
            </div>

            <div
              class="preview-wrapper"
              @click="triggerFileInput"
              @keydown.enter.prevent="triggerFileInput"
              @keydown.space.prevent="triggerFileInput"
              role="button"
              tabindex="0"
            >
              <canvas ref="frameCanvas" class="preview-canvas"></canvas>
              <div v-if="!uploadedFile" class="preview-placeholder">
                <div>
                  <i class="bi bi-cloud-upload fs-1 mb-2"></i>
                  <p class="mb-0">Click to upload your photo</p>
                  <small class="text-muted"
                    >Recommended: clear portrait photo (1:1)</small
                  >
                </div>
              </div>
              <div v-if="uploadedFile" class="preview-hover-hint">
                <i class="bi bi-arrow-repeat fs-3"></i>
                <p class="mb-0 small">Click to change photo</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.instruction-list {
  padding-left: 1.25rem;
}

.instruction-list li {
  margin-bottom: 0.5rem;
  line-height: 1.6;
}

.social-tags {
  background: #f8f9fa;
  padding: 1rem;
  border-radius: 8px;
  border-left: 4px solid #0d6efd;
}

.preview-wrapper {
  position: relative;
  width: 100%;
  max-width: 520px;
  background: #f8f9fa;
  border-radius: 12px;
  overflow: hidden;
  border: 2px dashed #dee2e6;
  cursor: pointer;
  transition: all 0.3s ease;
}

.preview-wrapper:hover {
  border-color: #0d6efd;
  box-shadow: 0 4px 12px rgba(13, 110, 253, 0.15);
}

.preview-wrapper::before {
  content: "";
  display: block;
  padding-top: 125%;
}

.preview-canvas {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  display: block;
}

.preview-placeholder {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6c757d;
  font-size: 0.95rem;
  text-align: center;
  flex-direction: column;
}

.preview-hover-hint {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.preview-wrapper:hover .preview-hover-hint {
  opacity: 1;
}
</style>
